<script lang="ts">
import wait from '@/modules/common/services/wait';
import { DialogFormStatus } from '@/modules/common/types/dialog';
import { OmsOrderStatus, OmsOrderType } from '@/connect/gen/consts/omsconsts_pb';
import Decimal from 'decimal.js';
import { computed, shallowRef, watch, watchEffect } from 'vue';
import { AgTableClient } from '@/modules/common/components/ag-table';
import * as cols from '@/modules/common/components/ag-table/columns/marketplace-batch-modify-all-dialog';
import { ColDef } from 'ag-grid-enterprise';
import MultipleChipAgreementSelector from '@/modules/agreements/components/MultipleChipAgreementSelector.vue';
import isEqual from 'lodash/isEqual';
import { UUID_EMPTY } from '@/constants';
import { FrontendOrder } from '@/connect/gen/modules/apiengine/services/oms/oms_pb';
import { i18nServerMessage } from '@/utils/helpers/rest-response';
import { AxiosError } from 'axios';
import { AgreementInfo } from '@/modules/agreements/models';
import { ModifyOmsOrdersRequest } from '@/modules/marketplace/models';
import { rateTypeOptions } from '@/modules/marketplace/helpers/marketplace';
import { isOrderRateEqual } from '@/modules/marketplace/helpers/batch-modify-rate';
import { RateType } from '@/connect';
import { Benchmark } from '@/utils/api/loans';
import { useBenchmarkPrice } from '@/modules/marketdata/composables/benchmarks';
import { useRateTypeStore } from '@/store/rate-type-store';
import { maximumValue, minimumValue, useRules } from '@/composables/vuetify-validation';
import { useMarketplaceApiService } from '@/modules/marketplace/services/marketplace-api.service';
import { RATE_PRECISION } from '@/modules/common/constants/precision';

const activateOptions: Array<{ label: string; value: boolean }> = [
  { label: 'Activate', value: true },
  { label: 'Deactivate', value: false },
];
</script>

<script setup lang="ts">
const rateTypeStore = useRateTypeStore();

const props = defineProps<{ items: FrontendOrder[] }>();

const emit = defineEmits<{ (e: 'close-modal' | 'success'): void }>();

const newAgreements = shallowRef<AgreementInfo[]>([]);
const resetAgreement = shallowRef(
  AgreementInfo.fromData({ id: 'CLEAR', shortName: 'Clear/Remove Agreement' })
);

const newFee = shallowRef<Decimal | null>(null);
const newRate = shallowRef<Decimal | null>(null);
const newMinQty = shallowRef<number | null>(null);
const active = shallowRef<boolean | null>(null);
const showSuccess = shallowRef(false);
const validationError = shallowRef<string | null>(null);

const apiErrors = shallowRef<string[]>([]);
const failedOrderRefs = shallowRef<string[]>([]);

const itemsToBeModified = shallowRef<FrontendOrder[]>(props.items);

const benchmarkPrice = useBenchmarkPrice(Benchmark.OBFR);

watchEffect(() => {
  if (newRate.value === null) {
    newFee.value = null;
    return;
  }
  newFee.value =
    rateTypeStore.marketplaceRateType === RateType.FEE
      ? newRate.value
      : (benchmarkPrice.value?.minus(newRate.value) ?? null);
});

// Clear validation error when fields are modified
watch([newRate, newMinQty, active], () => {
  validationError.value = null;
});

function isMinQuantityEqual(item: FrontendOrder): boolean {
  const minQuantity = Number(item.minQuantity);
  if (
    (newMinQty.value === 0 && minQuantity === 1) ||
    (newMinQty.value === 1 && minQuantity === 0)
  ) {
    return true;
  }
  return newMinQty.value === minQuantity;
}

const ordersWithEqualFields = computed(
  () =>
    new Map(
      itemsToBeModified.value
        .map(
          (item) =>
            [
              item,
              {
                status:
                  active.value !== null &&
                  ((item.orderStatus !== OmsOrderStatus.HELD && active.value) ||
                    (item.orderStatus === OmsOrderStatus.HELD && !active.value)),
                agreements: areAgreementsEqual(item),
                minQuantity: isMinQuantityEqual(item),
                fee: isOrderRateEqual(item, newRate.value, rateTypeStore.marketplaceRateType),
              },
            ] as const
        )
        .filter(
          ([_, { status, agreements, minQuantity, fee }]) =>
            status || agreements || minQuantity || fee
        )
    )
);

const showUnchangedFieldsDisclaimer = computed(() => ordersWithEqualFields.value.size > 0);

const touchedCount = computed(() => {
  const fields = [
    active.value !== null,
    newAgreements.value.length > 0,
    newMinQty.value !== null,
    newRate.value !== null,
  ];
  return fields.filter((field) => !!field).length;
});

const itemsToSubmit = computed(() =>
  itemsToBeModified.value.filter((item) => {
    return (
      Object.values(ordersWithEqualFields.value.get(item) ?? {}).filter(
        (isFieldEqual) => isFieldEqual
      ).length < touchedCount.value
    );
  })
);

const smallestRemainingQuantityInBatchSelection = computed(() => {
  const batchRemainingQty = itemsToBeModified.value.map(
    (item) => Number(item.quantity) - Number(item.totalExecQty)
  );
  return Math.min(...batchRemainingQty);
});

const areAllOrdersMarket = computed(() =>
  itemsToBeModified.value.every((item) => item.orderType === OmsOrderType.MARKET)
);

const columnDefs = computed<ColDef[]>(() => {
  const config = {
    ordersWithEqualFields: ordersWithEqualFields.value,
  };
  return [
    cols.status(config),
    cols.side(),
    cols.ticker(),
    cols.cusip(),
    cols.agreements(config),
    cols.executed(),
    cols.quantity(),
    cols.minQuantity(config),
    cols.fee(config),
    cols.rebate(config),
  ];
});

function addAgreement(agreement: AgreementInfo): void {
  validationError.value = null;

  if (agreement.id == UUID_EMPTY) {
    // ignore previous added agreements and only add resetCounterparty
    newAgreements.value = [resetAgreement.value];
  } else if (newAgreements.value[0] === resetAgreement.value) {
    // ignore resetCounterparty and only add the new agreement
    newAgreements.value = [agreement];
  } else {
    newAgreements.value.push(agreement);
  }
}

function removeAgreement(agreement: AgreementInfo): void {
  validationError.value = null;

  newAgreements.value = newAgreements.value.filter((a) => a.id !== agreement.id);
}

function areAgreementsEqual(item: FrontendOrder): boolean {
  if (newAgreements.value.length === 0) {
    return false;
  }
  if (newAgreements.value[0] === resetAgreement.value && item.agreementIds.length === 0) {
    return true;
  }
  return isEqual([...newAgreements.value].map((ag) => ag.id).sort(), [...item.agreementIds].sort());
}

function closeDialog() {
  emit('close-modal');
}

const { rules, validateAndSubmit } = useRules({
  newMinQty: {
    rules: [
      (v: string) => minimumValue(Number(v), 0),
      (v: string) => maximumValue(Number(v), smallestRemainingQuantityInBatchSelection.value),
    ],
  },
});

const formStatus = shallowRef<DialogFormStatus>('idle');

async function submitForm() {
  // Reset validation error
  validationError.value = null;

  // Check if at least one field was touched
  if (touchedCount.value === 0) {
    validationError.value = 'Please fill in at least one of the fields';
    return;
  }

  // Check if there are items to submit
  if (itemsToSubmit.value.length === 0) {
    validationError.value =
      'All items have the same values as the target fields. No changes to submit.';
    return;
  }

  await validateAndSubmit(async () => {
    formStatus.value = 'submitting';
    try {
      const request = ModifyOmsOrdersRequest.fromModel({
        orderRefs: itemsToSubmit.value.map((item) => item.orderRef),
        agreementIds: !newAgreements.value.length ? null : newAgreements.value.map((ag) => ag.id),
        minQuantity: newMinQty.value,
        rate: newFee.value,
        active: active.value === null ? null : active.value,

        // keep as is
        independentAmountRate: null,
        isAnonymous: null,
        orderType: null,
        quantity: null,
        roundingRule: null,
        settlementType: null,
        timeInForceType: null,
      });

      const res = await useMarketplaceApiService().batchUpdate(request);
      if (res.failedOrderRefs) {
        failedOrderRefs.value = res.failedOrderRefs;
        formStatus.value = 'idle';
      } else {
        showSuccess.value = true;
        formStatus.value = 'closing';
        await wait(800);
        emit('success');
        closeDialog();
      }
    } catch (err) {
      apiErrors.value = [i18nServerMessage(err as AxiosError)];
      formStatus.value = 'idle';
    }
  });
}
</script>

<template>
  <VDialog
    v-shortkey="['esc']"
    content-class="au-popup-dialog"
    :data-test-id="'my-dialog'"
    max-width="1100"
    overlay-color="secondary"
    overlay-opacity="0.80"
    persistents
    :value="true"
    @click:outside="closeDialog()"
    @keydown.esc="closeDialog()"
    @shortkey.native="closeDialog()"
  >
    <VForm novalidate @submit.prevent>
      <VCard v-shortkey="['enter']" class="form-summary" @shortkey="submitForm()">
        <VCardTitle class="mb-0">
          <div class="d-flex align-center">
            <span class="headline"> Modify {{ itemsToBeModified.length }} orders </span>
          </div>
          <VSpacer />
        </VCardTitle>
        <VDivider />

        <VCardText>
          <VAlert v-if="failedOrderRefs.length" dense type="error">
            {{ itemsToBeModified.length - failedOrderRefs.length }}
            {{ itemsToBeModified.length - failedOrderRefs.length > 1 ? 'actions' : 'action' }}
            succeeded / {{ failedOrderRefs.length }} failed (marked in red below)
          </VAlert>
          <VCardText v-else>
            <div class="d-flex pxas-4 gap-1">
              <template v-if="!areAllOrdersMarket">
                <VSelect
                  v-model="rateTypeStore.marketplaceRateType"
                  data-test="rate-type"
                  :items="rateTypeOptions"
                  label="Rate Type"
                  style="max-width: 6rem"
                  tabindex="-1"
                />
                <NumericInput
                  v-model="newRate"
                  data-test="rate"
                  :label="
                    rateTypeStore.marketplaceRateType === RateType.REBATE
                      ? 'Rebate Rate'
                      : 'Fee Rate'
                  "
                  :precision="RATE_PRECISION"
                  :step="0.25"
                  suffix="%"
                  type="decimal"
                />
              </template>
              <MultipleChipAgreementSelector
                :agreements="newAgreements"
                clearable
                label="Agreements"
                :prepend-item="resetAgreement"
                @add="addAgreement"
                @remove="removeAgreement"
              />
              <NumericInput
                v-model="newMinQty"
                v-bind="rules['newMinQty']"
                label="Min. Quantity"
                :max="smallestRemainingQuantityInBatchSelection"
                :min="1"
                :step="100"
                type="integer"
              />
              <VSelect
                v-model="active"
                clearable
                item-text="label"
                item-value="value"
                :items="activateOptions"
                label="Activate/Deactivate"
              />
            </div>
          </VCardText>
          <VContainer>
            <VDivider />
            <div class="table-container">
              <AgTableClient
                :column-defs="columnDefs"
                :get-row-id="(item) => item.orderRef"
                no-menu
                no-move
                no-sort
                :page-size="100"
                :row-class-rules="{
                  'error--text': (params) => failedOrderRefs.includes(params.data.orderRef),
                }"
                :row-data="itemsToBeModified"
                :table-id="'batch-modify-all-dialog-table'"
              />
            </div>
          </VContainer>

          <VRow v-if="showSuccess || validationError || showUnchangedFieldsDisclaimer">
            <VCol class="pa-0 px-1 pt-4 col-8 offset-2">
              <div
                class="v-alert v-alert--dense text--primary text-body-2 text-center"
                :class="{
                  success: showSuccess,
                  error: validationError || showUnchangedFieldsDisclaimer,
                }"
              >
                <div v-if="showSuccess">Modify all successfully initiated.</div>
                <div v-if="validationError">{{ validationError }}</div>
                <div v-if="showUnchangedFieldsDisclaimer && formStatus === 'idle'">
                  Fields marked in red already have the target value and won't be affected.
                </div>
              </div>
            </VCol>
          </VRow>
        </VCardText>

        <VCardActions class="d-flex px-8 py-4">
          <div v-if="failedOrderRefs.length" class="d-flex flex-grow-1 justify-end align-end">
            <VBtn color="primary" data-test="close-btn" @click="closeDialog()">Back</VBtn>
          </div>
          <div v-else class="d-flex flex-grow-1 justify-space-between align-end">
            <VBtn color="secondary" data-test="cancel-btn" @click="closeDialog">Back</VBtn>
            <VBtn
              color="primary"
              :disabled="formStatus !== 'idle'"
              :loading="formStatus === 'submitting'"
              type="submit"
              @click="submitForm()"
            >
              Confirm
            </VBtn>
          </div>
        </VCardActions>
        <VProgressLinear v-if="formStatus !== 'idle'" color="primary" indeterminate />
      </VCard>
    </VForm>
  </VDialog>
</template>

<style lang="scss" scoped>
.table-container {
  display: grid;
  min-height: 30vh;
}

.summary strong {
  color: red;
  font-weight: bold;
}

.gap-1 {
  gap: 1rem;
}
</style>
